/*
  Plantsgiving: share your personal giveaway link as an Instagram / Facebook / WhatsApp Story.

  Opened from the giveaway emails as /pages/plantsgiving-share?me={{ person.KlaviyoID }}.
  The parameter is ?me= on purpose, NOT ?ref=: snippets/giveaway-referral.liquid stores any
  valid ?ref= as "referred by", so a sharer opening their own page with ?ref= would end up
  referred by themselves (tests/giveaway-story-share.test.mjs pins this).

  What a browser can and cannot do (researched 20 Oct 2026):
  - No web page can post straight into a Story or pre-fill Instagram's link sticker; Meta's
    "Sharing to Stories" API is native-app only. So the flow is: one tap copies the link AND
    opens the phone's share sheet with a ready-made Story image; the user picks Instagram >
    Story, adds the link sticker and pastes.
  - navigator.share() needs a fresh tap, so the image File is rendered BEFORE the click; the
    click handler only copies + shares (no awaits in front of share()).
  - Without file sharing (desktop, Firefox, some in-app browsers) the page falls back to
    "save image + copy link"; on a computer it asks to open the email on the phone.
*/
(function (root) {
  'use strict';

  var ULID = /^[0-9A-Z]{26}$/;
  var GIVEAWAY_PATH = '/pages/giveaway';
  // utm_source per button, so GA4 shows which share route brings sign-ups.
  var SOURCES = { story: 'story_share', whatsapp: 'whatsapp', facebook: 'facebook', copy: 'copy_link' };

  function sharerId(search) {
    var raw = (new URLSearchParams(search || '').get('me') || '').trim().toUpperCase();
    return ULID.test(raw) ? raw : '';
  }

  // The link the FRIEND opens. Only a valid Klaviyo profile ID becomes ?ref= (same rule as the
  // referral snippet); anything else gives a plain giveaway link that credits nobody.
  function buildShareUrl(origin, id, channel) {
    var u = new URL(GIVEAWAY_PATH, origin);
    if (id && ULID.test(id)) u.searchParams.set('ref', id);
    u.searchParams.set('utm_source', SOURCES[channel] || 'share');
    u.searchParams.set('utm_medium', 'referral_share');
    u.searchParams.set('utm_campaign', 'plantsgiving_referral');
    return u.toString();
  }

  // ---------------------------------------------------------------- story image (1080 x 1920)
  var C = { green: '#6CB66C', ink: '#483C46', beige: '#F8F4ED', orange: '#FF6600', white: '#FFFFFF', cream: '#F8F4ED', red: '#F83030' };

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }

  // " | " in a text forces a line break (so a short last word never ends up alone)
  function wrap(ctx, text, maxWidth) {
    if (text.indexOf(' | ') > -1) {
      return text.split(' | ').reduce(function (all, part) { return all.concat(wrap(ctx, part, maxWidth)); }, []);
    }
    var words = text.split(' '), lines = [], line = '';
    words.forEach(function (w) {
      var t = line ? line + ' ' + w : w;
      if (ctx.measureText(t).width > maxWidth && line) { lines.push(line); line = w; } else { line = t; }
    });
    if (line) lines.push(line);
    return lines;
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  // Layout keeps Instagram's safe zones free: the top ~220px (profile bar) and the bottom
  // ~150px (reply bar) carry nothing important.
  //  layout 'green' (default): green poster block on top, the photo flows out of it.
  //  layout 'photo': the photo runs full-bleed behind the text, darkened at the top.
  //  focusY (0..1): which part of the photo stays in view when it is cropped (0 top, 1 bottom).
  //  zoom (>=1): enlarge the photo so focusY has room to move it; shade (0..1): darkening behind the text.
  async function renderStory(opts) {
    var font = opts.font || 'Poppins';
    var F = function (w, px) { return w + ' ' + px + 'px "' + font + '", Verdana, sans-serif'; };
    if (document.fonts && document.fonts.load) {
      try { await Promise.all([document.fonts.load(F(700, 80)), document.fonts.load(F(400, 40))]); } catch (e) { /* fallback font */ }
    }
    var W = 1080, H = 1920, PHOTO_END = 1500;
    var layout = opts.layout === 'photo' ? 'photo' : 'green';
    var focusY = typeof opts.focusY === 'number' ? Math.min(1, Math.max(0, opts.focusY)) : 0.5;
    var zoom = typeof opts.zoom === 'number' && opts.zoom >= 1 ? opts.zoom : 1;
    var shade = typeof opts.shade === 'number' ? Math.min(0.95, Math.max(0, opts.shade)) : 0.78;
    var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d');
    var t = opts.text;
    var photo = null, logo = null;
    try { photo = await loadImage(opts.photo); } catch (e) { /* no photo */ }
    try { logo = await loadImage(opts.logo); } catch (e) { /* no logo */ }

    function drawPhoto(top, bottom) {
      if (!photo) return;
      var ph = bottom - top, scale = Math.max(W / photo.width, ph / photo.height) * zoom;
      var sw = W / scale, sh = ph / scale, sx = (photo.width - sw) / 2, sy = (photo.height - sh) * focusY;
      ctx.drawImage(photo, sx, sy, sw, sh, 0, top, W, ph);
    }

    ctx.fillStyle = C.beige; ctx.fillRect(0, 0, W, H);
    if (layout === 'photo') {
      ctx.fillStyle = C.green; ctx.fillRect(0, 0, W, PHOTO_END);
      drawPhoto(0, PHOTO_END);
      var dark = ctx.createLinearGradient(0, 0, 0, 1080);
      dark.addColorStop(0, 'rgba(30,24,28,' + shade + ')'); dark.addColorStop(0.62, 'rgba(30,24,28,' + (shade * 0.7).toFixed(2) + ')'); dark.addColorStop(1, 'rgba(30,24,28,0)');
      ctx.fillStyle = dark; ctx.fillRect(0, 0, W, 1080);
    } else {
      ctx.fillStyle = C.green; ctx.fillRect(0, 0, W, PHOTO_END);
    }

    // white logo on top, then kicker, headline and subline (compact, so the photo gets the room)
    var y = 220;
    if (logo) { var lw = 250, lh = lw * logo.height / logo.width; ctx.drawImage(logo, (W - lw) / 2, y, lw, lh); y += lh + 58; } else { y += 50; }
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    if (layout === 'photo') { ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 18; }
    ctx.fillStyle = C.cream; ctx.font = F(700, 38);
    ctx.fillText(t.kicker, W / 2, y);
    y += 100;
    ctx.fillStyle = C.white; ctx.font = F(700, 88);
    wrap(ctx, t.headline, W - 140).forEach(function (l) { ctx.fillText(l, W / 2, y); y += 98; });
    ctx.font = F(400, 40);
    wrap(ctx, t.sub, W - 180).forEach(function (l) { ctx.fillText(l, W / 2, y); y += 52; });
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0;

    if (layout === 'green') {
      // photo flowing out of the green block
      var photoTop = y + 4;
      drawPhoto(photoTop, PHOTO_END);
      var fade = (PHOTO_END - photoTop) * 0.32;
      var g = ctx.createLinearGradient(0, photoTop, 0, photoTop + fade);
      g.addColorStop(0, C.green); g.addColorStop(1, 'rgba(108,182,108,0)');
      ctx.fillStyle = g; ctx.fillRect(0, photoTop, W, fade);
    }

    // red bar with the draw date (the website's Plantsgiving red)
    ctx.fillStyle = C.red; ctx.fillRect(0, PHOTO_END, W, 96);
    ctx.fillStyle = C.cream; ctx.font = F(700, 40);
    ctx.fillText(t.bar, W / 2, PHOTO_END + 62);

    // call to action + the spot for Instagram's link sticker
    ctx.fillStyle = C.ink; ctx.font = F(700, 58);
    ctx.fillText(t.cta, W / 2, 1666);
    ctx.save();
    ctx.setLineDash([18, 14]); ctx.lineWidth = 5; ctx.strokeStyle = C.orange;
    roundRect(ctx, 200, 1694, W - 400, 110, 55); ctx.stroke();
    ctx.restore();

    return new Promise(function (resolve) {
      cv.toBlob(function (blob) { resolve({ blob: blob, canvas: cv }); }, 'image/jpeg', 0.9);
    });
  }

  // ---------------------------------------------------------------- page wiring
  function track(channel) {
    try { if (root.klaviyo && root.klaviyo.push) root.klaviyo.push(['track', 'Plantsgiving Share Click', { channel: channel }]); } catch (e) { /* noop */ }
    try { if (typeof root.gtag === 'function') root.gtag('event', 'share', { method: channel, content_type: 'plantsgiving_referral' }); } catch (e) { /* noop */ }
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return false; });
    try {
      var ta = document.createElement('textarea'); ta.value = text; ta.setAttribute('readonly', '');
      ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select();
      var ok = document.execCommand('copy'); document.body.removeChild(ta); return Promise.resolve(ok);
    } catch (e) { return Promise.resolve(false); }
  }

  function init(el) {
    var d = el.dataset;
    var id = sharerId(location.search);
    var t = JSON.parse(el.querySelector('[data-story-text]').textContent);
    var link = function (ch) { return buildShareUrl(location.origin, id, ch); };
    var $ = function (s) { return el.querySelector(s); };
    var toast = $('[data-toast]');
    var say = function (msg) { toast.textContent = msg; toast.hidden = false; clearTimeout(say.t); say.t = setTimeout(function () { toast.hidden = true; }, 6000); };

    if (!id) $('[data-no-id]').hidden = false;
    $('[data-link]').textContent = link('copy');

    var file = null;
    var shareBtn = $('[data-share-story]');
    renderStory({ text: t, photo: d.photo, logo: d.logo, font: d.font, layout: d.layout,
      focusY: parseFloat(d.focusY), zoom: parseFloat(d.zoom), shade: parseFloat(d.shade) }).then(function (r) {
      file = new File([r.blob], 'plantsgiving-story.jpg', { type: 'image/jpeg' });
      var img = $('[data-preview]'); img.src = URL.createObjectURL(r.blob); img.hidden = false;
      $('[data-download]').href = img.src;
      var canFiles = !!(navigator.canShare && navigator.canShare({ files: [file] }));
      el.classList.add(canFiles ? 'can-share-files' : 'no-share-files');
      shareBtn.disabled = false;
    });

    shareBtn.addEventListener('click', function () {
      var url = link('story');
      track('story');
      // copy first (does not use up the tap), then share() synchronously inside the same tap
      copyText(url);
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        // files only: with text attached some iOS targets (Instagram) drop or misplace the image;
        // the link travels via the clipboard into Instagram's link sticker instead
        navigator.share({ files: [file] })
          .then(function () { say(t.toastShared); })
          .catch(function (e) { if (!e || e.name !== 'AbortError') say(t.toastFallback); });
        say(t.toastCopied);
      } else {
        el.classList.add('show-fallback');
        say(t.toastFallback);
        $('[data-fallback]').scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    $('[data-copy]').addEventListener('click', function () {
      track('copy');
      copyText(link('copy')).then(function (ok) { say(ok ? t.toastLinkCopied : t.toastCopyFailed); });
    });
    $('[data-whatsapp]').addEventListener('click', function () {
      track('whatsapp');
      root.open('https://wa.me/?text=' + encodeURIComponent(t.shareText + ' ' + link('whatsapp')), '_blank', 'noopener');
    });
    $('[data-facebook]').addEventListener('click', function () {
      track('facebook');
      root.open('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(link('facebook')), '_blank', 'noopener,width=640,height=560');
    });
    $('[data-download]').addEventListener('click', function () { track('download'); copyText(link('story')); say(t.toastSaved); });

  }

  var api = { sharerId: sharerId, buildShareUrl: buildShareUrl, renderStory: renderStory, ULID: ULID };
  root.SybStoryShare = api;
  if (typeof document !== 'undefined' && document.querySelectorAll) {
    var boot = function () { Array.prototype.forEach.call(document.querySelectorAll('[data-story-share]'), init); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  }
})(typeof window !== 'undefined' ? window : globalThis);
